import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

interface NodeDataPoint {
  id: string
  label: string
}

interface NodeItem {
  id: string
  label: string
  dataPoints: NodeDataPoint[]
}

interface NodeReferenceSelectorDialogProps {
  visible: boolean
  onHide: () => void
  availableNodes: NodeItem[]
  onSave: (selected: Record<string, string[]>) => void
  initialSelected?: Record<string, string[]>
}

const NodeReferenceSelectorDialog: React.FC<NodeReferenceSelectorDialogProps> = ({
  visible,
  onHide,
  availableNodes,
  onSave,
  initialSelected,
}) => {
  const [selected, setSelected] = useState<Record<string, Set<string>>>({})

  useEffect(() => {
    if (visible) {
      const initial: Record<string, Set<string>> = {}
      availableNodes.forEach((node) => {
        initial[node.id] = new Set()
      })
      if (initialSelected) {
        Object.entries(initialSelected).forEach(([nodeId, dpIds]) => {
          initial[nodeId] = new Set(dpIds)
        })
      }
      setSelected(initial)
    }
  }, [visible, availableNodes, initialSelected])

  const toggleDataPoint = (nodeId: string, dataPointId: string) => {
    setSelected((prev) => {
      const updated = { ...prev }
      if (!updated[nodeId]) updated[nodeId] = new Set()
      if (updated[nodeId].has(dataPointId)) {
        updated[nodeId].delete(dataPointId)
      } else {
        updated[nodeId].add(dataPointId)
      }
      return updated
    })
  }

  const handleSave = () => {
    const result: Record<string, string[]> = {}
    Object.entries(selected).forEach(([nodeId, setVal]) => {
      if (setVal.size > 0) {
        result[nodeId] = Array.from(setVal)
      }
    })
    onSave(result)
    onHide()
  }

  return (
    <Dialog open={visible} onOpenChange={(v) => !v && onHide()}>
      <DialogContent className="max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Select References</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 p-1">
            {availableNodes.map((node) => (
              <div key={node.id} className="border rounded p-3 bg-gray-50">
                <div className="font-bold mb-2">{node.label || node.id}</div>
                <div className="grid grid-cols-2 gap-2 ml-2">
                  {node.dataPoints.map((dp) => (
                    <label key={dp.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={selected[node.id]?.has(dp.id) || false}
                        onCheckedChange={() => toggleDataPoint(node.id, dp.id)}
                      />
                      <span>{dp.label || dp.id}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onHide}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NodeReferenceSelectorDialog
